import React from 'react';
import './ListingDetailModal.css'; // Importing for basic modal overlay styles only

// --- Icons ---
const CloseIcon = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>);
const TrendUpIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>);
const AlertIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d93c23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>);
const DollarIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>);
const BuildingIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#006aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="9" y1="22" x2="9" y2="22.01"></line><line x1="15" y1="22" x2="15" y2="22.01"></line><line x1="12" y1="22" x2="12" y2="22.01"></line><line x1="12" y1="2" x2="12" y2="6"></line><line x1="8" y1="6" x2="16" y2="6"></line></svg>);

interface Props {
  address: string;
  impactLevel: string; // e.g. "High Impact"
  nearbyCount: number;
  onClose: () => void;
  viewMode: string; // 'Buyer' or 'Investor'
}

const AddressImpactModal = ({ address, impactLevel, nearbyCount, onClose, viewMode }: Props) => {
  const isInvestor = viewMode === 'Investor';

  return (
    <div className="modal-overlay" onClick={onClose} style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(30, 41, 59, 0.7)' }}>
      <div 
        className="modal-container" 
        onClick={(e) => e.stopPropagation()} 
        style={{ 
          maxWidth: '1200px', 
          width: '98vw', 
          height: '94vh', 
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F8FAFC', 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }}
      >
        
        {/* HEADER */}
        <div style={{ backgroundColor: '#fff', padding: '32px', borderBottom: '1px solid #E2E8F0', position: 'relative' }}>
          <button 
            onClick={onClose}
            style={{ 
              position: 'absolute', top: '24px', right: '24px', 
              background: '#F1F5F9', border: 'none', borderRadius: '50%', 
              width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#64748B'
            }}
          >
            <CloseIcon />
          </button>

          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
            {isInvestor ? 'Investment Intelligence Report' : 'Development Impact Analysis'}
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#0F172A', margin: '0 0 16px 0', lineHeight: 1.2 }}>
            {address}
          </h1>
          
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {/* Dynamic Badges */}
            {isInvestor ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#F0FDF4', padding: '6px 12px', borderRadius: '6px', border: '1px solid #BBF7D0' }}>
                  <TrendUpIcon />
                  <span style={{ color: '#166534', fontWeight: 700, fontSize: '14px' }}>High Demand Area</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#EFF6FF', padding: '6px 12px', borderRadius: '6px', border: '1px solid #BFDBFE' }}>
                  <BuildingIcon />
                  <span style={{ color: '#1E40AF', fontWeight: 700, fontSize: '14px' }}>Renovation Potential</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#FEF2F2', padding: '6px 12px', borderRadius: '6px', border: '1px solid #FECACA' }}>
                  <AlertIcon />
                  <span style={{ color: '#991B1B', fontWeight: 700, fontSize: '14px' }}>High Construction Activity</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#F0FDF4', padding: '6px 12px', borderRadius: '6px', border: '1px solid #BBF7D0' }}>
                  <TrendUpIcon />
                  <span style={{ color: '#166534', fontWeight: 700, fontSize: '14px' }}>Appreciation Forecast</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* BODY */}
        <div className="modal-body" style={{ padding: '0', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          
          {/* EXECUTIVE SUMMARY */}
          <div style={{ padding: '32px', backgroundColor: '#fff', borderBottom: '1px solid #E2E8F0' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', marginTop: 0, marginBottom: '12px' }}>Executive Summary</h3>
            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#334155', margin: 0 }}>
              {isInvestor 
                ? "This asset is situated in a primary gentrification zone. The approval of the 220-unit 'Pointe at Main' development has established a new price ceiling for the neighborhood. We project a ripple effect lifting the value of adjacent single-family inventory by 12-15% as new retail anchor tenants stabilize."
                : "This property is currently situated in an Active Development Zone. While immediate quality-of-life impacts (noise, traffic) will be elevated for the next 14 months, the incoming mixed-use supply is projected to increase neighborhood desirability, driving an estimated 5-year appreciation of 12% (vs 7% area average)."
              }
            </p>
          </div>

          {/* TWO COLUMN ANALYSIS */}
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            
            {/* LEFT COLUMN */}
            <div style={{ flex: '1 1 400px', padding: '32px', borderRight: '1px solid #E2E8F0', backgroundColor: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isInvestor ? '#166534' : '#EF4444' }}></div>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', margin: 0 }}>
                  {isInvestor ? 'Market Catalysts' : 'Short Term Disruption'}
                </h4>
              </div>

              {isInvestor ? (
                // INVESTOR LEFT COLUMN
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>📈 Rental Demand</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>AGGRESSIVE</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      New luxury apartments nearby are renting for high prices. This makes <strong>older, affordable homes</strong> like this one very attractive to renters.
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🏗️ Max Resale Price</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>INCREASING</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Expensive new projects nearby prove that buyers are willing to pay premium prices to live on this street.
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🏙️ Future Zoning</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>FAVORABLE</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Proximity to the 'Area D' Redevelopment Zone increases probability of getting approvals for additions or expansions.
                    </div>
                  </div>
                </>
              ) : (
                // BUYER LEFT COLUMN (Existing)
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🔊 Acoustic Impact</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#D93C23', backgroundColor: '#FEE2E2', padding: '2px 8px', borderRadius: '4px' }}>SEVERE</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Heavy rock drilling scheduled at "The Pointe" (0.1 miles). Expect volume levels similar to a <strong>gas lawnmower</strong> running continuously between 7am-3pm.
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🚗 Traffic Pattern</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#D97706', backgroundColor: '#FEF3C7', padding: '2px 8px', borderRadius: '4px' }}>ELEVATED</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Construction vehicles utilizing Washington Ave. Expect <strong>5-10 min delays</strong> during morning rush (8am-9am).
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>💨 Air Quality</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>LOW RISK</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Demolition phase complete. Dust control measures currently active and compliant.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div style={{ flex: '1 1 400px', padding: '32px', backgroundColor: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isInvestor ? '#1E40AF' : '#22C55E' }}></div>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', margin: 0 }}>
                  {isInvestor ? 'Investment Potential' : 'Long Term Value'}
                </h4>
              </div>

              {isInvestor ? (
                // INVESTOR RIGHT COLUMN
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🔨 Renovation Strategy</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#1E40AF', backgroundColor: '#EFF6FF', padding: '2px 8px', borderRadius: '4px' }}>VALUE-ADD</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      The new development sets a higher standard for the area. Updating this home's kitchen and baths to match will justify a higher resale price.
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>📉 Rental Ease</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>HIGH DEMAND</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Very low vacancy rates in this town mean it will be easy to find qualified tenants quickly.
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🛡️ Neighborhood Stability</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>HIGH</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Large institutional investments into the block (like The Pointe) confirm that this neighborhood is on the rise.
                    </div>
                  </div>
                </>
              ) : (
                // BUYER RIGHT COLUMN (Existing)
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>📈 Appreciation Forecast</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>+12% (5yr)</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Introduction of Class-A anchor tenants typically lifts surrounding single-family home values by <strong>3-5% post-completion</strong>.
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🚶 Walkability Score</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '4px' }}>IMPROVING</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      New retail frontage includes proposed coffee shop and pharmacy, increasing local amenity access.
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#334155' }}>🎒 School Zone Impact</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#3B82F6', backgroundColor: '#DBEAFE', padding: '2px 8px', borderRadius: '4px' }}>STABLE</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
                      Hoover Elementary is currently under capacity. New units are primarily 1-BR, yielding low student generation.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* VALUATION FORECAST */}
          <div style={{ padding: '32px', backgroundColor: '#fff', borderTop: '1px solid #E2E8F0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
              <div style={{ padding: '6px', borderRadius: '6px', backgroundColor: '#F0FDF4' }}><DollarIcon /></div>
              <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', margin: 0 }}>
                Valuation Forecast
              </h4>
            </div>

            {/* Content Container */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '40px', flexWrap: 'wrap' }}>
              
              {/* Data Group: Current -> Future */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '32px' }}>
                {/* Current Value */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>Current Estimated Value</div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#334155' }}>$545,000</div>
                </div>

                {/* Arrow */}
                <div style={{ color: '#CBD5E1', display: 'flex', alignItems: 'center', marginTop: '22px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>

                {/* Future Value */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#166534', marginBottom: '4px' }}>Projected Value (2030)</div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#166534' }}>$610,400</div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#166534', marginTop: '4px' }}>▲ 12% Growth</div>
                </div>
              </div>

              {/* Context Blurb */}
              <div style={{ 
                flex: 1, 
                minWidth: '280px', 
                backgroundColor: '#F8FAFC', 
                padding: '16px', 
                borderRadius: '8px', 
                border: '1px solid #E2E8F0', 
                fontSize: '13px', 
                color: '#475569', 
                lineHeight: '1.5',
                marginTop: '-18px' 
              }}>
                <strong>Resitus Insight:</strong> {isInvestor 
                  ? "Instant Equity: This property is currently trading at a 15% discount relative to the projected price per square foot of the neighborhood post-development (2027)."
                  : "Based on historical data of homes within 0.2 miles of Class-A mixed-use anchors, this property is projected to outperform the area market average by an additional $27,250 in equity over the next 5 years."
                }
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddressImpactModal;